// ═══════════════════════════════════════════════════════════════════════
// 📋 COMPREHENSIVE QA REGISTRY
// جميع الصفحات CRUD في التطبيق
// ✅ كل أسماء الـ endpoints تم تأكيدها من ملفات الـ backend routers الفعلية
// ═══════════════════════════════════════════════════════════════════════

import { CrudTestConfig } from "./entityCrudTestHelper";

// ─── Import all Axios instances ──────────────────────────────────────────
import { IcdNotCoveredAxiosInstance } from "../../Network/IcdNotCoveredAxiosInstance";
import { ActivityNotCoveredAxiosInstance } from "../../Network/ActivityNotCoveredAxiosInstance";
import { ActivityNotAllowedAxiosInstance } from "../../Network/ActivityNotAllowedAxiosInstance";
import { ActivityAllowedICDAxiosInstance } from "../../Network/ActivityAllowedICDAxiosInstance";
import { ActivityNotAllowedICDAxiosInstance } from "../../Network/activityNotAllowedICDAxiosInstance";
import { ActivityNotAllowedIncludeActivityAxiosInstance } from "../../Network/ActivityNotAllowedIncludeActivityAxiosInstance";
import { ActivityAllowedAxiosInstance } from "../../Network/ActivityAllowedAxiosInstance";

import { BenefitGroupAxiosInstance } from "../../Network/BenefitGroupAxiosInstance";
import { BenefitAxiosInstance } from "../../Network/BenefitAxiosInstance";
import { PrincipalDiagnosedAxiosInstance } from "../../Network/PrincipalDiagnosedAxiosInstance";

import { NewNetworkAxiosInstance } from "../../Network/NewNetworkAxiosInstance";
import { providerAxiosInstance } from "../../Network/providerAxiosInstance";
import { InsuranceAxiosInstance } from "../../Network/InsuranceAxiosInstance";
import { SpecialtyAxiosInstance } from "../../Network/SpecialtyAxiosInstance";
import { VipAxiosInstance } from "../../Network/VipAxiosInstance";

import { DoctorAxiosInstance } from "../../Network/DoctorAxiosInstance";
import { PolicyAxiosInstance } from "../../Network/PolicyAxiosInstance";
import { PatientRelationAxiosInstance } from "../../Network/PatientRelationAxiosInstance";

// ═══════════════════════════════════════════════════════════════════════
// 🗂️ COMPLETE QA REGISTRY
// ═══════════════════════════════════════════════════════════════════════
//
// hasSoftDelete: false  →  الصفحة عندها delete واحد بس (مافي soft/restore)،
//                          فالـ helper بيتخطّى اختبارات softDelete/restore.
// ═══════════════════════════════════════════════════════════════════════

export const COMPLETE_QA_REGISTRY: Record<string, CrudTestConfig> = {
  // ═══ ICD MANAGEMENT ═══════════════════════════════════════════════════

  "icd-not-covered": {
    pageName: "ICD Not Covered",
    axiosInstance: IcdNotCoveredAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      add: "addNewIcdNotCovered",
      update: "updateIcdNotCovered",
      fetch: "getIcdNotCovered",
      softDelete: "softDeleteIcdNotCovered/{id}",
      hardDelete: "deleteIcdNotCovered/{id}",
      restore: "restoreIcdNotCovered/{id}",
    },
  },

  // ═══ ACTIVITY MANAGEMENT ═════════════════════════════════════════════

  "activity-not-covered": {
    pageName: "Activity Not Covered",
    axiosInstance: ActivityNotCoveredAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      add: "addNewActivityNotCovered",
      update: "updateActivityNotCovered",
      fetch: "getActivityNotCovered",
      softDelete: "softDeleteActivityNotCovered/{id}",
      hardDelete: "deleteActivityNotCovered/{id}",
      restore: "restoreActivityNotCovered/{id}",
    },
  },

  "activity-allowed": {
    pageName: "Activity Allowed",
    axiosInstance: ActivityAllowedAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      add: "addNewActivityAllowed",
      update: "updateActivityAllowed",
      fetch: "getActivityAllowed",
      softDelete: "softDeleteActivityAllowed/{id}",
      hardDelete: "deleteActivityAllowed/{id}",
      restore: "restoreActivityAllowed/{id}",
    },
  },

  "activity-not-allowed": {
    pageName: "Activity Not Allowed",
    axiosInstance: ActivityNotAllowedAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      add: "addNewActivityNotAllowed",
      update: "updateActivityNotAllowed",
      fetch: "getActivityNotAllowed",
      softDelete: "softDeleteActivityNotAllowed/{id}",
      hardDelete: "deleteActivityNotAllowed/{id}",
      restore: "restoreActivityNotAllowed/{id}",
    },
  },

  "activity-allowed-icd": {
    pageName: "Activity Allowed ICD",
    axiosInstance: ActivityAllowedICDAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      add: "addNewActivityAllowedIcd",
      update: "updateActivityAllowedIcd",
      fetch: "getActivityAllowedIcd",
      softDelete: "softDeleteActivityAllowedIcd/{id}",
      hardDelete: "deleteActivityAllowedIcd/{id}",
      restore: "restoreActivityAllowedIcd/{id}",
    },
  },

  "activity-not-allowed-icd": {
    pageName: "Activity Not Allowed ICD",
    axiosInstance: ActivityNotAllowedICDAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      add: "addNewActivityNotAllowedIcd",
      update: "updateActivityNotAllowedIcd",
      fetch: "getActivityNotAllowedIcd",
      softDelete: "softDeleteActivityNotAllowedIcd/{id}",
      hardDelete: "deleteActivityNotAllowedIcd/{id}",
      restore: "restoreActivityNotAllowedIcd/{id}",
    },
  },

  "activity-not-allowed-include-activity": {
    pageName: "Activity Bundle",
    axiosInstance: ActivityNotAllowedIncludeActivityAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      add: "addNewActivityNotAllowedIncludeActivity",
      update: "updateActivityNotAllowedIncludeActivity",
      fetch: "getActivityNotAllowedIncludeActivity",
      softDelete: "softDeleteActivityNotAllowedIncludeActivity/{id}",
      hardDelete: "deleteActivityNotAllowedIncludeActivity/{id}",
      restore: "restoreActivityNotAllowedIncludeActivity/{id}",
    },
  },

  // ═══ BENEFIT MANAGEMENT ══════════════════════════════════════════════

  "benefit-group": {
    pageName: "Benefit Group",
    axiosInstance: BenefitGroupAxiosInstance,
    hasSoftDelete: false, // ✅ مافي soft/restore بالـ backend
    endpoints: {
      add: "addNewBenefitGroup",
      update: "updateBenefitGroup",
      fetch: "getBenefitGroup",
      hardDelete: "deleteBenefitGroup/{id}",
      softDelete: "",
      restore: "",
    },
  },

  "benefit": {
    pageName: "Benefit",
    axiosInstance: BenefitAxiosInstance,
    hasSoftDelete: true, // ✅ الـ backend عنده soft/restore (حتى لو الصفحة ما بتستخدمهم)
    endpoints: {
      add: "addNewBenefit",
      update: "updateBenefit",
      fetch: "getBenefit",
      softDelete: "softDeleteBenefit/{id}",
      hardDelete: "deleteBenefit/{id}",
      restore: "restoreBenefit/{id}",
    },
  },

  // ⚠️ Benefit Template — مشلوحة حسب طلبك (ما إلها داعي)
  // وكمان الـ backend مالهاش soft/restore (addNew + delete بس)

  "principal-diagnosed": {
    pageName: "Principal Diagnosed",
    axiosInstance: PrincipalDiagnosedAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      add: "addNewPrincipalDiagnosed",
      update: "updatePrincipalDiagnosed",
      fetch: "getPrincipalDiagnosed",
      // ⚠️ انتبه: أسماء soft/restore فيها "IcdNot" بالنص (هيك هي بالـ backend فعلاً)
      softDelete: "softDeleteIcdNotPrincipalDiagnosed/{id}",
      hardDelete: "deletePrincipalDiagnosed/{id}",
      restore: "restoreIcdNotPrincipalDiagnosed/{id}",
    },
  },

  // ═══ CONFIGURATION ═══════════════════════════════════════════════════

  "new-network": {
    pageName: "New Network",
    axiosInstance: NewNetworkAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      add: "addNewNetwork",
      update: "updateNetwork",
      fetch: "getNetwork",
      softDelete: "softDeleteNetwork/{id}",
      hardDelete: "deleteNetwork/{id}",
      restore: "restoreNetwork/{id}",
    },
  },

  "provider": {
    pageName: "Provider",
    axiosInstance: providerAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      add: "addNewProvider",
      update: "updateProvider",
      fetch: "getProvider",
      softDelete: "softDeleteProvider/{id}",
      hardDelete: "deleteProvider/{id}",
      restore: "restoreProvider/{id}",
    },
  },

  "insurance": {
    pageName: "Insurance",
    axiosInstance: InsuranceAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      // ⚠️ مهم: الاسم الفعلي InsurancePlan مش Insurance!
      add: "addNewInsurancePlan",
      update: "updateInsurancePlan",
      fetch: "getInsurancePlan",
      softDelete: "softDeleteInsurancePlan/{id}",
      hardDelete: "deleteInsurancePlan/{id}",
      restore: "restoreInsurancePlan/{id}",
    },
  },

  "specialty": {
    pageName: "Specialty",
    // ⚠️ احذف نسخة وحدة من SpecialtyAxiosInstance (.ts أو .tsx)
    axiosInstance: SpecialtyAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      add: "addNewSpecialty",
      update: "updateSpecialty",
      fetch: "getSpecialty",
      softDelete: "softDeleteSpecialty/{id}",
      hardDelete: "deleteSpecialty/{id}",
      restore: "restoreSpecialty/{id}",
    },
  },

  "vip": {
    pageName: "VIP",
    axiosInstance: VipAxiosInstance,
    hasSoftDelete: true,
    endpoints: {
      // ⚠️ ملاحظة: ما ظهر addNewVip بالـ grep — تأكد من اسم add/update/fetch بملف Vip.ts
      add: "addNewVip",
      update: "updateVip",
      fetch: "getVip",
      softDelete: "softDeleteVip/{id}",
      hardDelete: "deleteVip/{id}",
      restore: "restoreVip/{id}",
    },
  },

  // ═══ MASTER / LOOKUP PAGES ═══════════════════════════════════════════

  "doctor": {
    pageName: "Doctor",
    axiosInstance: DoctorAxiosInstance,
    hasSoftDelete: true,
    payloadStyle: "wrapped", // Doctor بيستخدم { data: [...], token }
    endpoints: {
      add: "addNewDoctor",
      update: "updateDoctor",
      fetch: "getDoctor",
      softDelete: "softDeleteDoctor/{id}",
      hardDelete: "deleteDoctor/{id}",
      restore: "restoreDoctor/{id}",
    },
  },

  "policy": {
    pageName: "Policy",
    axiosInstance: PolicyAxiosInstance,
    hasSoftDelete: true,
    payloadStyle: "flat", // ⚠️ Policy بيبعت الحقول مباشرة { ...fields, token }
    endpoints: {
      add: "addpolicy",       // ⚠️ حرف صغير
      update: "updatepolicy", // ⚠️ حرف صغير
      fetch: "getpolicy",     // ⚠️ حرف صغير
      softDelete: "softDeletePolicy/{id}",
      hardDelete: "deletePolicy/{id}",
      restore: "restorePolicy/{id}",
    },
  },

  "patient": {
    pageName: "Patient",
    axiosInstance: PatientRelationAxiosInstance,
    hasSoftDelete: true,
    payloadStyle: "flat", // ⚠️ Patient بيبعت الحقول مباشرة { ...fields, token }
    endpoints: {
      add: "add",          // ⚠️ اسمها "add" بس!
      update: "update",    // ⚠️ اسمها "update" بس!
      fetch: "getpatient",
      softDelete: "softDeletePatient/{id}",
      hardDelete: "deletePatient/{id}",
      restore: "restorePatient/{id}",
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // 📌 صفحات موجودة بالـ backend بس مش بالـ Dashboard / محتاجة instances:
  //    - icd-allowed       (addNewIcdAllowed ...)        → موجود بالـ backend
  //    - icd-not-allowed   (addNewIcdNotAllowed ...)     → موجود بالـ backend
  //    - medical-icd-groups (addNewMedicalGroup ...)     → موجود بالـ backend
  //    - custom-icd-groups (addNewCustomGroup ...)       → موجود بالـ backend
  //    لو بدك تضيفهم، احتاج أسماء الـ AxiosInstance تبعهم.
  // ═══════════════════════════════════════════════════════════════════════
};

// ═══════════════════════════════════════════════════════════════════════
// 🔧 DEFAULT TEST PAYLOADS  (نمط wrapped — relation-style fields)
// ═══════════════════════════════════════════════════════════════════════

const defaultPayload = (namePrefix: string) => ({
  relation_name: `QA_TEST_${namePrefix}_${Date.now()}`,
  icd: ["TEST_A00"],
  custom_groups: [],
  medical_groups: [],
  provider: [],
  provider_mode: "DISABLED",
  provider_all: false,
  network: [],
  network_mode: "DISABLED",
  network_all: false,
  insuranceplan: [],
  insuranceplan_mode: "DISABLED",
  insuranceplan_all: false,
  payer: [],
  payer_mode: "DISABLED",
  payer_all: false,
  patient: [],
  patient_mode: "DISABLED",
  patient_all: false,
  policy: [],
  policy_mode: "DISABLED",
  policy_all: false,
  user_level: [],
  user_mode: "DISABLED",
  user_all: false,
  vip: [],
  vip_mode: "DISABLED",
  vip_all: false,
  doctor: [],
  doctor_mode: "DISABLED",
  doctor_all: false,
  specialty: [],
  specialty_mode: "DISABLED",
  specialty_all: false,
  is_active: false,
});

export const COMPLETE_TEST_PAYLOADS: Record<string, any> = {
  "icd-not-covered": defaultPayload("ICD_NOT_COVERED"),
  "activity-not-covered": defaultPayload("ACTIVITY_NOT_COVERED"),
  "activity-allowed": defaultPayload("ACTIVITY_ALLOWED"),
  "activity-not-allowed": defaultPayload("ACTIVITY_NOT_ALLOWED"),
  "activity-allowed-icd": defaultPayload("ACTIVITY_ALLOWED_ICD"),
  "activity-not-allowed-icd": defaultPayload("ACTIVITY_NOT_ALLOWED_ICD"),
  "activity-not-allowed-include-activity": defaultPayload("ACTIVITY_INCLUDE_ACTIVITY"),
  "benefit-group": defaultPayload("BENEFIT_GROUP"),
  "benefit": defaultPayload("BENEFIT"),
  "principal-diagnosed": defaultPayload("PRINCIPAL_DIAGNOSED"),
  "new-network": defaultPayload("NETWORK"),

  // ⚠️ master/lookup pages → غالباً schema مختلفة، عدّل الحقول حسب الـ backend لو فشلت
  "provider": defaultPayload("PROVIDER"),
  "insurance": defaultPayload("INSURANCE"),
  "specialty": defaultPayload("SPECIALTY"),
  "vip": defaultPayload("VIP"),

  // ═══ MASTER PAGES — payloads مخصّصة بحقولها الحقيقية ═══════════════════
  // (مأخوذة من ملفات الصفحات الفعلية)

  // Doctor → wrapped: { data: [{...}], token }
  "doctor": {
    Doctor_name: `QA_Doctor_${Date.now()}`,
    Doctor_id: `QADOC${Date.now()}`,
    specialty: [],          // array of specialty ids
    Status: false,
  },

  // Policy → flat: { ...fields, token }
  "policy": {
    Policy_name: `QA_Policy_${Date.now()}`,
    Policy_id: `QAPOL${Date.now()}`,
    Status: "Inactive",
    Start_date: "2025-01-01",
    End_date: "2025-12-31",
    Plan: [],
    Network: [],
  },

  // Patient → flat: { ...fields, token }
  "patient": {
    National_id: Date.now() % 1000000000,
    Member_id: (Date.now() + 1) % 1000000000,
    Patient_passport: `QA${Date.now() % 100000}`,
    Patient_dob: "1990-01-01",
    Gender: "male",
    First_name: "QaTest",
    Last_name: "Patient",
    Full_name: "QaTest Patient",
    Policy: [],
    Total_Coverage: 10000,
    Current_spending: 0,
    Status: false,
  },
};

// ═══════════════════════════════════════════════════════════════════════
// 📊 STATS
// ═══════════════════════════════════════════════════════════════════════

export const QA_STATS = {
  totalPages: Object.keys(COMPLETE_QA_REGISTRY).length,
  lastUpdated: new Date().toISOString(),
};

console.log(`📊 QA Registry Loaded: ${QA_STATS.totalPages} pages ready for testing`);